/**
 * Per-slot targeting for the Custom Ads page. Mirrors the backend's slot
 * fields (`segments`, `excludePayers`, `minLevel`, `experiment`, `countries`):
 * a slot with none of them is shown to everyone, exactly as before targeting
 * existed.
 *
 * Test arms: several slots on one Slot Index, each tagged with the same
 * experiment and a different variant. The backend serves a player the first
 * of them that matches their variant, or the `control` arm when the test is
 * not running or their variant is unknown.
 */

/** The spender segments the backend accepts besides cohort slugs. */
export const SPENDER_SEGMENTS = ['non-payer', 'minnow', 'dolphin', 'whale'] as const;

/** Highest level the backend accepts for `minLevel`. */
export const MAX_MIN_LEVEL = 100;

/** The variant the backend serves when a test is off or a player's variant is unknown. */
export const EXPERIMENT_CONTROL = 'control';

const COUNTRY_CODE = /^[A-Z]{2}$/;

export interface SlotExperiment {
  key: string;
  variant: string;
}

/** include: only players placed in these countries (unknown country = hidden); exclude: not these. */
export interface SlotCountries {
  include?: string[];
  exclude?: string[];
}

export interface SlotTargeting {
  segments?: string[];
  excludePayers?: boolean;
  minLevel?: number | null;
  /** Test arm: only players in this variant see the slot (control also when the test is off). */
  experiment?: { key?: string | null; variant?: string | null } | null;
  countries?: { include?: string[] | null; exclude?: string[] | null } | null;
}

/** An experiment the slot picker offers (`GET /admin/experiments`). */
export interface ExperimentOption {
  key: string;
  status: string;
  variants: string[];
}

/** The slot's experiment when both its key and variant are set; anything less is no test. */
export function slotExperimentOf(slot: SlotTargeting | null | undefined): SlotExperiment | null {
  const key = slot?.experiment?.key;
  const variant = slot?.experiment?.variant;
  return typeof key === 'string' && key && typeof variant === 'string' && variant
    ? { key, variant }
    : null;
}

function countryList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const item of list) {
    const code = typeof item === 'string' ? item.trim().toUpperCase() : '';
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

/** Upper-cased, de-duplicated lists; empty lists left out; null when no list is left. */
export function cleanCountries(countries: SlotTargeting['countries']): SlotCountries | null {
  const include = countryList(countries?.include);
  const exclude = countryList(countries?.exclude);
  const out: SlotCountries = {};
  if (include.length > 0) out.include = include;
  if (exclude.length > 0) out.exclude = exclude;
  return out.include || out.exclude ? out : null;
}

/** Experiments out of a `GET /admin/experiments` body (a bare list or `{ data: [...] }`). */
export function experimentOptionsFromResponse(body: unknown): ExperimentOption[] {
  const root = body as { data?: unknown } | null | undefined;
  const rows: unknown[] = Array.isArray(body) ? body : Array.isArray(root?.data) ? root.data : [];
  return rows
    .map((row) => row as { key?: unknown; status?: unknown; variants?: unknown } | null)
    .filter((row): row is { key: string; status?: unknown; variants?: unknown } =>
      Boolean(row && typeof row.key === 'string' && row.key !== '')
    )
    .map((row) => ({
      key: row.key,
      status: typeof row.status === 'string' ? row.status : 'unknown',
      variants: (Array.isArray(row.variants) ? row.variants : [])
        .map((variant) => (variant as { key?: unknown } | null)?.key)
        .filter((key): key is string => typeof key === 'string' && key !== ''),
    }));
}

/** Spender segments first, then cohort slugs; a slug that repeats a segment is listed once. */
export function segmentOptions(cohortSlugs: string[]): Array<{ label: string; value: string }> {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const value of [...SPENDER_SEGMENTS, ...cohortSlugs]) {
    if (typeof value === 'string' && value && !seen.has(value)) {
      seen.add(value);
      values.push(value);
    }
  }
  return values.map((value) => ({ label: value, value }));
}

/** The cohort rows of a `GET /admin/cohorts` body: `{ cohorts, total }`, `{ data: [...] }` or a bare list. */
function cohortRows(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  const root = body as { data?: unknown; cohorts?: unknown; items?: unknown } | null | undefined;
  const list = [root?.cohorts, root?.data, root?.items].find((candidate) =>
    Array.isArray(candidate)
  );
  return Array.isArray(list) ? list : [];
}

/** `total` from the body, or from a `meta` / `data` wrapper; undefined when the server sent none. */
function cohortTotal(body: unknown): number | undefined {
  const root = body as
    | { total?: unknown; meta?: { total?: unknown }; data?: { total?: unknown } }
    | null
    | undefined;
  const total = [root?.total, root?.meta?.total, root?.data?.total].find(
    (candidate) => typeof candidate === 'number'
  );
  return typeof total === 'number' ? total : undefined;
}

/** Cohort slugs out of a `GET /admin/cohorts` body. */
export function cohortSlugsFromResponse(body: unknown): string[] {
  return cohortRows(body)
    .map((row) => (row as { slug?: unknown } | null)?.slug)
    .filter((slug): slug is string => typeof slug === 'string' && slug !== '');
}

/** The server caps the cohorts list at 100 rows per call. */
export const COHORT_PAGE_SIZE = 100;
/** Safety cap: 10 pages = 1000 cohorts. */
export const COHORT_MAX_PAGES = 10;

/**
 * Loads every cohort slug, one page of `pageSize` at a time via `fetchPage(offset)`.
 * Stops when a page is short or empty, when `total` is reached, or after `maxPages`.
 * A failing page ends the load and keeps the slugs already read.
 */
export async function loadAllCohortSlugs(
  fetchPage: (offset: number) => Promise<unknown>,
  pageSize: number = COHORT_PAGE_SIZE,
  maxPages: number = COHORT_MAX_PAGES
): Promise<string[]> {
  const slugs: string[] = [];
  let offset = 0;
  for (let page = 0; page < maxPages; page += 1) {
    let body: unknown;
    try {
      body = await fetchPage(offset);
    } catch (error) {
      console.error('Failed to fetch a page of cohorts for slot targeting', error);
      break;
    }
    const rows = cohortRows(body).length;
    slugs.push(...cohortSlugsFromResponse(body));
    offset += rows;
    const total = cohortTotal(body);
    if (rows < pageSize || (total !== undefined && offset >= total)) break;
  }
  return slugs;
}

/**
 * Drops empty segments, excludePayers false, an empty minLevel, a half-set
 * experiment and empty country lists, so an untargeted slot saves as before.
 * Keys the page has no field for are kept as they are.
 */
export function cleanSlotTargeting<T extends object>(slot: T): T {
  const { segments, excludePayers, minLevel, experiment, countries, ...rest } = slot as Record<
    string,
    unknown
  >;
  const out: Record<string, unknown> = { ...rest };
  if (Array.isArray(segments) && segments.length > 0) out.segments = segments;
  if (excludePayers === true) out.excludePayers = true;
  if (typeof minLevel === 'number' && Number.isFinite(minLevel)) out.minLevel = minLevel;
  const exp = slotExperimentOf({ experiment: experiment as SlotTargeting['experiment'] });
  if (exp) out.experiment = exp;
  const country = cleanCountries(countries as SlotTargeting['countries']);
  if (country) out.countries = country;
  return out as T;
}

/**
 * One line for the slot card title, e.g. "test banner_store_v1: price · IN only"
 * or "dolphin, whale · not payers · level 5+".
 */
export function describeTargeting(slot: SlotTargeting): string {
  const parts: string[] = [];
  const exp = slotExperimentOf(slot);
  if (exp) parts.push(`test ${exp.key}: ${exp.variant}`);
  const country = cleanCountries(slot.countries);
  if (country?.include) parts.push(`${country.include.join(', ')} only`);
  if (country?.exclude) parts.push(`not ${country.exclude.join(', ')}`);
  if (slot.segments && slot.segments.length > 0) parts.push(slot.segments.join(', '));
  if (slot.excludePayers) parts.push('not payers');
  if (typeof slot.minLevel === 'number') parts.push(`level ${slot.minLevel}+`);
  return parts.length > 0 ? parts.join(' · ') : 'everyone';
}

export interface SlotProblems {
  /** The backend would refuse the save, or a slot would never show: fix before saving. */
  errors: string[];
  /** Saves fine, but some players see nothing on a slot index: confirm before saving. */
  warnings: string[];
}

/**
 * Checks the slots about to be saved. Errors mirror the backend's refusals
 * (bad country codes; a test index mixing experiments or plain slots; a test
 * index without a control arm) plus an arm whose variant the experiment does
 * not have. Warnings are holes the backend accepts: a variant with no arm on a
 * test index, or whose arms all carry an include-country list, so its players
 * get nothing there. `experiments` empty (list failed to load) skips the
 * variant checks. Slots are numbered as on the page (1-based).
 */
export function slotProblems(
  slots: Array<SlotTargeting & { sIndex?: number | null }> | null | undefined,
  experiments: ExperimentOption[]
): SlotProblems {
  const errors: string[] = [];
  const warnings: string[] = [];
  const list = slots ?? [];
  const known = new Map(experiments.map((e) => [e.key, e]));

  list.forEach((slot, i) => {
    const country = cleanCountries(slot.countries);
    for (const code of [...(country?.include ?? []), ...(country?.exclude ?? [])]) {
      if (!COUNTRY_CODE.test(code)) {
        errors.push(`Slot ${i + 1}: "${code}" is not a 2-letter country code (e.g. IN).`);
      }
    }
    const exp = slotExperimentOf(slot);
    const def = exp ? known.get(exp.key) : undefined;
    if (exp && def && def.variants.length > 0 && !def.variants.includes(exp.variant)) {
      errors.push(
        `Slot ${i + 1}: ${exp.key} has no variant "${exp.variant}" (it has ${def.variants.join(', ')}), so nobody would see this arm.`
      );
    }
  });

  const byIndex = new Map<number, Array<SlotTargeting & { sIndex?: number | null }>>();
  for (const slot of list) {
    if (typeof slot.sIndex !== 'number') continue;
    byIndex.set(slot.sIndex, [...(byIndex.get(slot.sIndex) ?? []), slot]);
  }
  for (const [sIndex, onIndex] of byIndex) {
    const exps = onIndex.map((slot) => slotExperimentOf(slot));
    if (exps.every((exp) => !exp)) continue; // plain slots: unchanged rules
    const keys = new Set(exps.map((exp) => exp?.key));
    if (keys.size !== 1 || exps.some((exp) => !exp)) {
      errors.push(
        `Slot index ${sIndex}: every slot on an index with test arms must carry the same experiment (found ${[...keys].map((k) => k ?? 'no experiment').join(', ')}).`
      );
      continue;
    }
    const key = [...keys][0] as string;
    const variants = new Set(exps.map((exp) => (exp as SlotExperiment).variant));
    if (!variants.has(EXPERIMENT_CONTROL)) {
      errors.push(
        `Slot index ${sIndex}: test ${key} has no "${EXPERIMENT_CONTROL}" arm. Everyone sees the control arm when the test is off, so it is required.`
      );
    }
    const def = known.get(key);
    if (!def) {
      if (experiments.length > 0) {
        warnings.push(
          `Slot index ${sIndex}: experiment ${key} was not found, so only the "${EXPERIMENT_CONTROL}" arm will show.`
        );
      }
      continue;
    }
    for (const variant of def.variants) {
      const arms = onIndex.filter((slot) => slotExperimentOf(slot)?.variant === variant);
      if (arms.length === 0) {
        warnings.push(
          `Slot index ${sIndex}: players in ${key} variant "${variant}" will see nothing here (no arm for them).`
        );
        continue;
      }
      const includes = arms.map((slot) => cleanCountries(slot.countries)?.include);
      if (includes.every((inc) => inc && inc.length > 0)) {
        const codes = [...new Set(includes.flat() as string[])].join(', ');
        warnings.push(
          `Slot index ${sIndex}: players in ${key} variant "${variant}" outside ${codes} will see nothing here.`
        );
      }
    }
  }
  return { errors, warnings };
}
